import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Loader2, CheckCircle, AlertTriangle, Clock, FileText, Shield,
  Users, MessageSquare, Zap, ChevronRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_CONFIG = {
  task_created: { icon: Clock, color: "text-blue-600", bg: "bg-blue-100" },
  task_updated: { icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-100" },
  condition_added: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-100" },
  condition_cleared: { icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-100" },
  condition_updated: { icon: FileText, color: "text-blue-600", bg: "bg-blue-100" },
  approval_recorded: { icon: Shield, color: "text-purple-600", bg: "bg-purple-100" },
  stage_change: { icon: ChevronRight, color: "text-cyan-600", bg: "bg-cyan-100" },
  message_sent: { icon: MessageSquare, color: "text-slate-600", bg: "bg-slate-100" },
  lender_matched: { icon: Users, color: "text-emerald-600", bg: "bg-emerald-100" },
  pipeline_run: { icon: Zap, color: "text-amber-600", bg: "bg-amber-100" },
  document_uploaded: { icon: FileText, color: "text-blue-600", bg: "bg-blue-100" },
};

const DEFAULT_CONFIG = { icon: Clock, color: "text-slate-600", bg: "bg-slate-100" };

export default function TimelinePanel({ dealId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("ic_token");

  const fetchTimeline = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/timeline/${dealId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEvents(res.data.events || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [dealId, token]);

  useEffect(() => { fetchTimeline(); }, [fetchTimeline]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="text-center py-12" data-testid="timeline-empty">
        <Clock className="h-10 w-10 text-[#ccc] mx-auto mb-3" />
        <p className="text-sm text-[#999]">No timeline events yet for this deal.</p>
        <p className="text-xs text-[#bbb] mt-1">Events will appear as actions are taken on this deal.</p>
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid="timeline-panel">
      <div className="relative pl-6">
        {/* Vertical line */}
        <div className="absolute left-[11px] top-2 bottom-2 w-px bg-cream-warm" />

        {events.map((event, i) => {
          const config = EVENT_CONFIG[event.event_type] || DEFAULT_CONFIG;
          const Icon = config.icon;
          const ts = event.created_at ? new Date(event.created_at) : null;

          return (
            <motion.div
              key={event.event_id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.03 }}
              className="relative flex items-start gap-3 py-2.5"
              data-testid={`timeline-event-${event.event_id}`}
            >
              {/* Dot */}
              <div className={`absolute -left-6 mt-0.5 h-[22px] w-[22px] rounded-full ${config.bg} flex items-center justify-center ring-2 ring-white`}>
                <Icon className={`h-3 w-3 ${config.color}`} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#333] leading-snug">{event.summary}</p>
                <div className="flex items-center gap-2 mt-1 text-[10px] text-[#aaa]">
                  <span className="px-1.5 py-0 rounded bg-cream border border-cream-warm font-medium text-[#777]">
                    {event.event_type.replace(/_/g, " ")}
                  </span>
                  {event.actor_type === "system" && (
                    <span className="px-1.5 py-0 rounded bg-amber-50 border border-amber-200 text-amber-600 font-medium">auto</span>
                  )}
                  {ts && (
                    <span>{ts.toLocaleDateString()} {ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
